import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Kbd } from './Kbd.js';
import { Spinner } from './Spinner.js';
import { cssVar } from '../theme/variables.js';
import { motion } from 'framer-motion';
import { css, styled } from 'styled-components';
import { mediaHover, mediaNotMobile } from '../constants/styled-utils.js';
import { useDefaultedEvent } from '@fabric-app/hooks/useDefaultedEvent';
import { preventForwardPropsConfig } from '../utils/preventForwardProps.js';

const ButtonRoundLevels = {
  default: cssVar["radius-input"],
  lg: cssVar["radius-input-lg"]
};
const btnHeight = {
  xss: "1.5rem",
  xs: "1.875rem",
  "sm-": "2rem",
  sm: "2.125rem",
  "sm+": "2.25rem",
  default: "2.5rem",
  "mobile-default": "2.5rem",
  lg: "3.125rem"
};
const btnPadding = {
  default: "0.625rem",
  lg: "1rem",
  "sm-": ".625rem",
  sm: ".625rem",
  "sm+": ".625rem",
  xs: "0.5rem",
  xss: "0.375rem",
  "mobile-default": "0.8125rem"
};
const btnFontSize = {
  default: "0.8125rem",
  lg: "0.9375rem",
  "mobile-default": "0.9375rem",
  "sm-": "0.8125rem",
  sm: "0.8125rem",
  "sm+": "0.8125rem",
  xs: "0.875rem",
  xss: "0.75rem"
};
const cssSizeProperties = css([
  ``,
  ``
], (p) => css([
  `height:`,
  `;width:`,
  `;padding:0 `,
  `;font-size:`,
  `;`,
  `;border-radius:`,
  `;`
], btnHeight[p.size || "default"], p.shape === "square" || p.shape === "round" ? btnHeight[p.size || "default"] : p.shape === "full" ? "100%" : "max-content", btnPadding[p.size || "default"], btnFontSize[p.size || "default"], p.size === "xs" && "font-weight: 500;", p.shape === "round" ? "100%" : ButtonRoundLevels[p.roundness || "default"]));
const cssBorder = css([
  ``,
  ``
], (p) => {
  switch (p.border) {
    case "default":
      return css([
        `border-color:`,
        `;`,
        `{&:hover:not(:disabled){border-color:`,
        `;}}`
      ], cssVar["color-border-primary"], mediaHover, cssVar["color-text-primary"]);
    case "none":
      return css([
        `border:none;`,
        `{&:hover:not(:disabled){border:none;}}`
      ], mediaHover);
  }
});
const cssDisabledStateForColoredButton = css([
  `&:disabled{background:`,
  `;cursor:not-allowed;color:white;`,
  `{color:rgba(255,255,255,0.8);background:hsla(`,
  `,0.2);svg{color:rgba(255,255,255,0.8) !important;}}`,
  `{&:hover:not(:disabled){box-shadow:none;}}}`
], cssVar["color-bg-disabled"], Kbd, cssVar["color-bg-primary-hsl"], mediaHover);
const cssColorProperties = css([
  ``,
  ``
], (p) => {
  switch (p.variant) {
    case "transparent": {
      return css([
        `background:transparent;color:`,
        `;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`,
        `{&:hover:not(:disabled){background:transparent;box-shadow:none;color:`,
        `;}}`,
        ``
      ], cssVar["color-text-tertiary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary-hsl"], mediaHover, cssVar["color-text-primary"], p.active && css([
        `&{background:`,
        `;color:`,
        `;}`,
        `{&:hover:not(:disabled){background:`,
        `;color:`,
        `;}}`
      ], cssVar["color-text-primary"], cssVar["color-bg-primary"], mediaHover, cssVar["color-text-primary"], cssVar["color-bg-primary"]));
    }
    case "primary-inverse": {
      return css([
        `background:`,
        `;color:`,
        `;`,
        `{&:hover:not(:disabled){background:#d3cbfb;}}&:disabled{background:`,
        `;color:rgba(`,
        `,0.2);}`
      ], cssVar["color-app-primary-cmp"], cssVar["color-app-primary"], mediaHover, cssVar["color-app-primary-cmp"], cssVar["color-app-primary-rgb"]);
    }
    case "green-inverse": {
      return css([
        `background:#e5ffe6;color:#008d0e;`,
        `{&:hover:not(:disabled){background:#ccffcf;}}`
      ], mediaHover);
    }
    case "danger-transparent": {
      return css([
        `color:#ff0000;`,
        `{&:hover:not(:disabled){background:`,
        `;color:#ff0000;box-shadow:none;}}`,
        ``
      ], mediaHover, cssVar["color-bg-tertiary"], p.active && css([
        `&{background:`,
        `;color:`,
        `;}`,
        `{&:hover:not(:disabled){background:`,
        `;color:`,
        `;}}`
      ], cssVar["color-text-primary"], cssVar["color-bg-primary"], mediaHover, cssVar["color-text-primary"], cssVar["color-bg-primary"]));
    }
    case "icon":
    case "semi-transparent": {
      return css([
        `color:`,
        `;`,
        `{&:hover:not(:disabled){background:hsla(`,
        `,0.1);box-shadow:none;}}kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`,
        ``
      ], cssVar["color-text-primary"], mediaHover, cssVar["color-bg-primary-reverse-hsl"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"], p.active && css([
        `&{background:`,
        `;color:`,
        `;}`,
        `{&:hover:not(:disabled){background:`,
        `;color:`,
        `;}}`
      ], cssVar["color-text-primary"], cssVar["color-bg-primary"], mediaHover, cssVar["color-text-primary"], cssVar["color-bg-primary"]));
    }
    case "bg-primary": {
      return css([
        `background-color:`,
        `;color:`,
        `;border:1px solid `,
        `;`
      ], cssVar["color-bg-primary"], cssVar["color-text-primary"], cssVar["color-border-primary"]);
    }
    case "bg-secondary": {
      return css([
        `background-color:`,
        `;color:`,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`,
        ` `,
        ``
      ], cssVar["color-bg-secondary-button"], cssVar["color-text-primary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"], p.muted && !p.active && css([
        `> svg{color:`,
        `;}color:`,
        `;`
      ], cssVar["color-text-primary"], cssVar["color-text-tertiary"]), p.active && css([
        `background-color:`,
        `;color:`,
        `;`
      ], cssVar["color-bg-primary-reverse"], cssVar["color-bg-secondary-button"]));
    }
    case "bg-tertiary": {
      return css([
        `background-color:`,
        `;color:`,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`
      ], cssVar["color-bg-tertiary"], cssVar["color-text-primary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"]);
    }
    case "bg-quarternary": {
      return css([
        `background-color:`,
        `;color:`,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`
      ], cssVar["color-bg-quarternary"], cssVar["color-text-primary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"]);
    }
    case "bg-quinary": {
      return css([
        `background-color:`,
        `;color:`,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`
      ], cssVar["color-bg-quinary"], cssVar["color-text-primary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"]);
    }
    case "bg-senary": {
      return css([
        `background-color:`,
        `;color:`,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`
      ], cssVar["color-bg-senary"], cssVar["color-text-primary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"]);
    }
    case "bg-contrast-reverse": {
      return css([
        `background-color:`,
        `;color:`,
        `;`,
        `{&:hover:not(:disabled){box-shadow:0 0 10rem 0 rgba(`,
        `,0.2) inset;}}`
      ], cssVar["color-bg-contrast-reverse"], cssVar["color-text-primary-reverse"], mediaHover, cssVar["color-bg-contrast-rgb"]);
    }
    case "outline-primary": {
      return css([
        `background-color:transparent;color:`,
        `;border:1px solid `,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}`
      ], cssVar["color-text-primary"], cssVar["color-text-primary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"]);
    }
    case "outline-secondary": {
      return css([
        `background-color:transparent;color:`,
        `;border:1px solid `,
        `;box-shadow:none;`
      ], cssVar["color-text-secondary"], cssVar["color-text-secondary"]);
    }
    case "outline-quaternary": {
      return css([
        `background-color:transparent;color:`,
        `;border:1px solid `,
        `;box-shadow:none;kbd{background:hsla(`,
        `,0.1);color:`,
        `;}&:disabled{border:1px solid rgba(`,
        `,0.2);color:rgba(`,
        `,0.4);}`
      ], cssVar["color-text-secondary"], cssVar["color-text-quaternary"], cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"], cssVar["color-text-primary-rgb"], cssVar["color-text-primary-rgb"]);
    }
    case "outline-placeholder": {
      return css([
        `background-color:transparent;color:`,
        `;border:1px solid `,
        `;box-shadow:none;`,
        `{&:hover:not(:disabled){color:`,
        `;}}`
      ], cssVar["color-text-placeholder"], cssVar["color-text-placeholder"], mediaHover, cssVar["color-text-tertiary"]);
    }
    case "bg-primary-reverse": {
      return css([
        `background-color:`,
        `;color:`,
        `;border:1px solid `,
        `;`,
        `{&:hover:not(:disabled){box-shadow:0 0 10rem 0 rgba(`,
        `,0.2) inset;}}`
      ], cssVar["color-bg-primary-reverse"], cssVar["color-bg-primary"], cssVar["color-border-primary"], mediaHover, cssVar["color-bg-primary-rgb"]);
    }
    case "danger": {
      return css([
        `background-color:#c72727;color:white;kbd{color:rgba(255,255,255,0.8);}`,
        `;`
      ], cssDisabledStateForColoredButton);
    }
    case "delete": {
      return css([
        `background-color:`,
        `;color:white;kbd{color:rgba(255,255,255,0.8);}`,
        `;`
      ], cssVar["color-app-delete"], cssDisabledStateForColoredButton);
    }
    case "bg-contrast-reverse-semi-transparent": {
      return css([
        `background:rgba(`,
        `,0.04);color:`,
        `;`,
        `;`
      ], cssVar["color-bg-contrast-reverse-rgb"], cssVar["color-text-primary"], cssDisabledStateForColoredButton);
    }
    case "primary":
    default: {
      return css([
        `background-color:`,
        `;color:white;`,
        `{background:hsla(`,
        `,0.2);color:rgba(255,255,255,0.8);svg{color:rgba(255,255,255,0.8) !important;opacity:0.8;}}`,
        ``
      ], cssVar["color-app-primary"], Kbd, cssVar["color-bg-primary-hsl"], cssDisabledStateForColoredButton);
    }
  }
});
const LoadingWrapper = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  },
  transition: {
    delay: 0,
    duration: 0.1
  }
}).withConfig({
  displayName: "Button__LoadingWrapper",
  componentId: "sc-cb69ed6c-0"
})([
  `position:absolute;top:0;left:0;bottom:0;right:0;display:flex;align-items:center;justify-content:center;background:inherit;`
]);
const getSpinnerColor = (variant) => {
  switch (variant) {
    case "primary":
    case "danger":
    case void 0:
    case "green-inverse":
      return "white";
    case "bg-contrast-reverse":
      return cssVar["color-bg-primary"];
    default:
      return void 0;
  }
};
const Button = styled.button.attrs(({ isLoading, isLoadingChildren, ...props }) => {
  return {
    ...props,
    isLoading,
    // @TODO loading state, e.g. spinner or custom texts
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        isLoading && /* @__PURE__ */ jsx(LoadingWrapper, {
          children: isLoadingChildren || /* @__PURE__ */ jsx(Spinner, {
            size: 20,
            color: getSpinnerColor(props.variant)
          })
        }),
        props.children
      ]
    }),
    onPointerUp: useDefaultedEvent((e) => {
      e.currentTarget.blur();
    }, props.onPointerUp)
  };
}).withConfig(preventForwardPropsConfig([
  "isLoading"
])).withConfig({
  displayName: "Button",
  componentId: "sc-cb69ed6c-1"
})([
  `width:max-content;flex-shrink:0;display:flex;align-items:center;justify-content:center;border-radius:`,
  `;gap:0.375rem;position:relative;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;font-weight:500;outline:0px solid transparent;transition:0.2s box-shadow 0s,0.2s outline 0.1s;&:focus{outline:none;}&:focus-visible{outline:4px solid rgba(128,128,128,0.2);}`,
  ` svg[data-fabric-icon]{font-size:1.125em;width:1.125em;height:auto;}kbd{background:hsla(`,
  `,0.1);color:`,
  `;}`,
  `;`,
  `{&:hover:not(:disabled){box-shadow:0 0 10rem 0 rgba(`,
  `,0.1) inset;}}&:disabled{background:`,
  `;cursor:not-allowed;color:`,
  `;`,
  `{color:rgba(255,255,255,0.8);background:hsla(`,
  `,0.2);svg{color:rgba(255,255,255,0.8);}}`,
  `{&:hover:not(:disabled){box-shadow:none;}}}`,
  `;`,
  `;`,
  ``
], cssVar["radius-input"], (p) => p.isLoading && css([
  `pointer-events:none;*{opacity:0.5;}`
]), cssVar["color-bg-primary-reverse-hsl"], cssVar["color-text-tertiary"], cssSizeProperties, mediaHover, cssVar["color-bg-primary-reverse-rgb"], cssVar["color-bg-secondary-button"], cssVar["color-text-placeholder"], Kbd, cssVar["color-bg-primary-hsl"], mediaHover, cssColorProperties, cssBorder, (p) => p.withKbd && css([
  ``,
  `{gap:0.6125rem;padding-left:0.6125rem;padding-right:0.6125rem;}`
], mediaNotMobile));
Button.defaultProps = {
  type: "button"
};
const ButtonIcon = styled(Button).attrs((props) => {
  return {
    size: "sm",
    shape: "square",
    variant: "icon",
    ...props
  };
}).withConfig({
  displayName: "Button__ButtonIcon",
  componentId: "sc-cb69ed6c-2"
})([
  ``
]);
const ButtonNavigational = styled(Button).attrs((props) => {
  return {
    variant: "primary-inverse",
    ...props
  };
}).withConfig({
  displayName: "Button__ButtonNavigational",
  componentId: "sc-cb69ed6c-3"
})([
  `padding:0 10px;font-size:14px;&:disabled{background-color:`,
  `;color:`,
  `;}`
], cssVar["color-bg-quarternary"], cssVar["color-text-primary"]);

export { Button, ButtonIcon, ButtonNavigational };
//# sourceMappingURL=Button.js.map
